import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { completeRegistration } from "../../utils/api";
import { getItemWithExpiry } from "../../utils/storage";
import IconButton from '@mui/material/IconButton';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import { CssBaseline, ThemeProvider, createTheme } from '@mui/material';

function CompleteRegistration() {
  const [formData, setFormData] = useState({ otp: "", password: "", confirmPassword: "" });
  const [response, setResponse] = useState(null);
  const [error, setError] = useState(null);
  const [themeMode, setThemeMode] = useState("dark");
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { otp, password, confirmPassword } = formData;

    if (password !== confirmPassword) {
      setError("Passwords do not match. Please try again.");
      return;
    }

    const tempID = getItemWithExpiry("tempID");
    if (!tempID) {
      setError("Temporary ID has expired or not found. Please register again.");
      return;
    }

    if (!otp || !password) {
      setError("OTP and password are required.");
      return;
    }

    try {
      const data = await completeRegistration({ tempID, otp, password });
      setResponse(data);
      setError(null);
      navigate("/login", { replace: true });
    } catch (error) {
      if (error.message.includes("Invalid OTP")) {
        setError("Invalid OTP or session expired. Please try again.");
      } else {
        setError("Failed to complete registration: " + error.message);
      }
      setResponse(null);
    }
  };

  const toggleTheme = () => {
    setThemeMode((prevMode) => (prevMode === "dark" ? "light" : "dark"));
  };

  const theme = createTheme({
    palette: {
      mode: themeMode,
    },
  });

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          background: themeMode === "light"
            ? "linear-gradient(135deg, #975cd7, #5a8fea)"
            : "linear-gradient(135deg, #121212, #333)",
          fontFamily: "Arial, sans-serif",
          color: themeMode === "light" ? "#333" : "#fff",
          position: "relative",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: "20px",
            left: "20px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <img
            src="/assets/logo.png"
            alt="Logo"
            style={{
              width: '50px',
              height: '50px',
              objectFit: 'contain',
            }}
          />
        </div>

        <div style={{ position: "absolute", top: 20, right: 20 }}>
          <IconButton onClick={toggleTheme} style={{ color: "#fff" }}>
            {themeMode === "light" ? (
              <DarkModeOutlinedIcon />
            ) : (
              <LightModeOutlinedIcon />
            )}
          </IconButton>
        </div>

        <form
          onSubmit={handleSubmit}
          style={{
            background: themeMode === "light" ? "rgba(255, 255, 255, 0.3)" : "rgba(0, 0, 0, 0.5)",
            padding: "2.5rem 3rem",
            borderRadius: "12px",
            boxShadow: "0 8px 32px rgba(31, 38, 135, 0.37)",
            width: "100%",
            maxWidth: "400px",
            display: "flex",
            flexDirection: "column",
            gap: "1.5rem",
            animation: "fadeIn 1s ease-in-out",
          }}
        >
          <h2 style={{ margin: "0", textAlign: "center" }}>
            🔒 Complete Registration
          </h2>

          <input
            type="text"
            name="otp"
            placeholder="📧 Enter OTP"
            value={formData.otp}
            onChange={handleChange}
            required
            style={{
              padding: "0.8rem 1rem",
              border: "1px solid #ddd",
              borderRadius: "6px",
              outline: "none",
              fontSize: "1rem",
              transition: "all 0.3s",
              background: themeMode === "light" ? "#fff" : "#333",
              color: themeMode === "light" ? "#333" : "#fff",
            }}
            onFocus={(e) => (e.target.style.borderColor = "#6dd5ed")}
            onBlur={(e) => (e.target.style.borderColor = "#ddd")}
          />

          <input
            type="password"
            name="password"
            placeholder="🔑 Enter Password"
            value={formData.password}
            onChange={handleChange}
            required
            style={{
              padding: "0.8rem 1rem",
              border: "1px solid #ddd",
              borderRadius: "6px",
              outline: "none",
              fontSize: "1rem",
              transition: "all 0.3s",
              background: themeMode === "light" ? "#fff" : "#333",
              color: themeMode === "light" ? "#333" : "#fff",
            }}
            onFocus={(e) => (e.target.style.borderColor = "#6dd5ed")}
            onBlur={(e) => (e.target.style.borderColor = "#ddd")}
          />

          <input
            type="password"
            name="confirmPassword"
            placeholder="🔒 Confirm Password"
            value={formData.confirmPassword}
            onChange={handleChange}
            required
            style={{
              padding: "0.8rem 1rem",
              border: "1px solid #ddd",
              borderRadius: "6px",
              outline: "none",
              fontSize: "1rem",
              transition: "all 0.3s",
              background: themeMode === "light" ? "#fff" : "#333",
              color: themeMode === "light" ? "#333" : "#fff",
            }}
            onFocus={(e) => (e.target.style.borderColor = "#6dd5ed")}
            onBlur={(e) => (e.target.style.borderColor = "#ddd")}
          />

          <button
            type="submit"
            style={{
              padding: "0.8rem 1rem",
              background: themeMode === 'light'
                ? 'linear-gradient(90deg, #975cd7, #5a8fea)'
                : 'linear-gradient(90deg, #333, #121212)',
              color: '#fff',
              fontSize: "1rem",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              transition: "transform 0.2s ease-in-out",
            }}
            onMouseOver={(e) => (e.target.style.transform = "scale(1.05)")}
            onMouseOut={(e) => (e.target.style.transform = "scale(1)")}
          >
            Complete Registration 🚀
          </button>

          <p style={{ textAlign: "center", marginTop: "1rem" }}>
            Already have an account?{" "}
            <Link
              to="/login"
              style={{
                color: themeMode === "light" ? "#000" : "#fff",
                textDecoration: "none",
                fontWeight: "bold",
              }}
            >
              Login here
            </Link>
          </p>

          {response && (
            <div
              style={{
                backgroundColor: "#e6ffe6",
                padding: "1rem",
                borderRadius: "6px",
                textAlign: "left",
                color: "green",
              }}
            >
              <p>✅ Registration completed successfully!</p>
              <pre style={{ whiteSpace: "pre-wrap", fontSize: "0.9rem" }}>
                {JSON.stringify(response.user, null, 2)}
              </pre>
            </div>
          )}

          {error && (
            <div
              style={{
                backgroundColor: "#ffe6e6",
                padding: "1rem",
                borderRadius: "6px",
                textAlign: "center",
                color: "red",
              }}
            >
              ❌ {error}
            </div>
          )}
        </form>
      </div>
    </ThemeProvider>
  );
}

export default CompleteRegistration;
